import { useCurrentOrganization, useLoggedInUsers, useUser } from '@meteor/state';
import { useEffect } from 'react';

let amplitudePromise = import('@amplitude/analytics-browser');

amplitudePromise.then(a =>
  a.init('3174bd233df9a2b422764de6ae180a30', {
    serverZone: 'EU'
  })
);

export let withAmplitude = async <T>(fn: (a: Awaited<typeof amplitudePromise>) => T) => {
  let a = await amplitudePromise;
  return fn(a);
};

export let setAuth = (i: {
  session: { id: string };
  user: { id: string; name: string; email: string };
  member?: { id: string; name: string; email: string };
}) =>
  withAmplitude(async amplitude => {
    amplitude.setDeviceId(i.session.id);
    amplitude.setUserId(i.member?.id ?? i.user.id);

    let identify = new amplitude.Identify()
      .set('name', i.user.name)
      .set('email', i.user.email)
      .set('sessionId', i.session.id)
      .set('userId', i.user.id);

    if (i.member) {
      identify.set('memberId', i.member.id);
      identify.set('memberName', i.member.name);
      identify.set('memberEmail', i.member.email);
    }

    amplitude.identify(identify);
  });

export let useInitializeAnalytics = () => {
  let user = useUser();
  let users = useLoggedInUsers();
  let org = useCurrentOrganization();

  useEffect(() => {
    if (!user.data || !users.data) return;

    let currentSession = users.data.allUserSessions.find(s => s.userId == user.data?.id);
    if (!currentSession) return;

    setAuth({
      user: user.data,
      session: currentSession,
      member: org.data?.membership
    });
  }, [user.data, users.data, org.data]);
};

export let useAnalytics = () => {
  let org = useCurrentOrganization();

  return {
    track: (p: Parameters<Awaited<typeof amplitudePromise>['track']>) => {
      withAmplitude(async amplitude => {
        amplitude.track(p[0], {
          ...p[1],

          organization: org.data
            ? {
                id: org.data.id,
                name: org.data.name,
                slug: org.data.slug,

                member: {
                  id: org.data.membership.id,
                  name: org.data.membership.name,
                  email: org.data.membership.email
                }
              }
            : null
        });
      }).catch(console.error);
    }
  };
};

export let useSendAnalytics = (
  provider: () => {
    event: string;
    properties: Record<string, any>;
  },
  deps: any[]
) => {
  let { track } = useAnalytics();

  useEffect(() => {
    let { event, properties } = provider();
    track([event, properties]);
  }, deps);
};

export let AnalyticsInitializer = () => {
  useInitializeAnalytics();
  return null;
};
